import { KHPoint } from '../..';

/**
 * A functional mixin to set any manipulative HTML element as draggable
 * @param element - Any HTML Element
 * @param onDragStart - Additional logic for drag start
 * @param onDrag - Additional logic that happens while dragging
 * @param onDragEnd - Additional logic for drag end
 */
export function SetDraggable(element: HTMLElement,
  onDragStart?: (pos: KHPoint, e: MouseEvent | TouchEvent) => void,
  onDrag?: (pos: KHPoint, e: MouseEvent | TouchEvent) => void,
  onDragEnd?: (pos: KHPoint, e: MouseEvent | TouchEvent) => void) {
  let isDragging = false;
  let startX = parseFloat(element.style.left);
  let startY = parseFloat(element.style.top);
  let startMouseX = 0;
  let startMouseY = 0;
  let scale = 1;

  // drag start
  const dragStartFn = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    isDragging = true;

    let startPos: KHPoint = { x: 0, y: 0 };
    if (e instanceof MouseEvent) {
      startPos = { x: e.clientX, y: e.clientY };
    } else if (e instanceof TouchEvent && e.touches.length > 0) {
      startPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }

    startX = parseFloat(element.style.left) || 0;
    startY = parseFloat(element.style.top) || 0;
    startMouseX = startPos.x;
    startMouseY = startPos.y;

    // Attach document-level listeners only when dragging starts
    document.addEventListener('mousemove', dragFn);
    document.addEventListener('touchmove', dragFn, { passive: false });
    document.addEventListener('mouseup', dragEndFn);
    document.addEventListener('touchend', dragEndFn);
    document.addEventListener('touchcancel', dragEndFn);

    if (onDragStart) {
      onDragStart({ x: startPos.x, y: startPos.y }, e);
    }

    // This is a CSS trick to compute the scale which we'll need to modify the drag position with.
    // We can't use getComputedStyle(element).scale because that's not the actual scale of the element.
    // Bascially, boundingClientRect is affected by parent scale but offsetWidth is not.
    // We always scale x and y the same so we can just use the width.
    const rect = element.getBoundingClientRect();
    scale = rect.width / element.offsetWidth;

    if (isNaN(scale)) {
      scale = 1;
    }
  };

  // on drag
  const dragFn = (e: MouseEvent | TouchEvent) => {
    if (isDragging) {
      e.preventDefault();
      let dragPos: KHPoint = { x: 0, y: 0 };

      if (e instanceof MouseEvent) {
        dragPos = { x: e.clientX, y: e.clientY };
      } else if (e instanceof TouchEvent && e.touches.length > 0) {
        dragPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else {
        return; // No valid touch point
      }

      const inverseScale = 1 / scale;

      const newLeft = startX + ((dragPos.x - startMouseX) * inverseScale);
      const newTop = startY - ((startMouseY - dragPos.y) * inverseScale);

      element.style.left = `${newLeft}px`;
      element.style.top = `${newTop}px`;

      if (onDrag) {
        onDrag({ x: dragPos.x * inverseScale, y: dragPos.y * inverseScale }, e);
      }
    }
  };

  // drag end
  const dragEndFn = (e: MouseEvent | TouchEvent) => {
    if (isDragging) {
      e.preventDefault();

      // Remove document-level listeners as soon as dragging ends
      document.removeEventListener('mousemove', dragFn);
      document.removeEventListener('touchmove', dragFn);
      document.removeEventListener('mouseup', dragEndFn);
      document.removeEventListener('touchend', dragEndFn);
      document.removeEventListener('touchcancel', dragEndFn);

      if (onDragEnd && (e instanceof MouseEvent || e instanceof TouchEvent)) {
        onDragEnd({ x: parseFloat(element.style.left) || 0, y: parseFloat(element.style.top) || 0 }, e);
      }
    }
    isDragging = false;
  };

  // Only attach element-level listeners initially
  // Document-level listeners are added/removed dynamically during drag
  element.addEventListener('mousedown', dragStartFn);
  element.addEventListener('touchstart', dragStartFn, { passive: false });

  return function removeDraggable() {
    element.removeEventListener('mousedown', dragStartFn);
    element.removeEventListener('touchstart', dragStartFn);

    // Clean up document listeners in case they're still attached
    document.removeEventListener('mousemove', dragFn);
    document.removeEventListener('touchmove', dragFn);
    document.removeEventListener('mouseup', dragEndFn);
    document.removeEventListener('touchend', dragEndFn);
    document.removeEventListener('touchcancel', dragEndFn);
  };
}
